// ============================================================
// store/snap-stash/context.tsx
//
// React Context exposing a per-instance snap-stash flow store.
// Mirrors `store/stash-card-detail/context.tsx`. The provider
// wires the real deps (service, canvas normalize, geolocation);
// tests construct the store directly with mocks.
// ============================================================

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useStore } from 'zustand';
import { createSnapStashStore, type SnapStashDeps, type SnapStashStoreApi } from './createSnapStashStore';
import { imageExtractionService } from '../../services/image-extraction-service';
import { normalizeImageFile } from '../../utils/image-normalize';
import { getLocationHint } from '../../utils/location-hint';
import type { SnapStashStore } from './types';

const SnapStashStoreContext = createContext<SnapStashStoreApi | null>(null);

const defaultDeps: SnapStashDeps = {
  service: imageExtractionService,
  normalize: normalizeImageFile,
  getLocation: getLocationHint,
};

interface SnapStashStoreProviderProps {
  /** Test seam — omit in production. */
  deps?: SnapStashDeps;
  children: ReactNode;
}

export function SnapStashStoreProvider({ deps, children }: SnapStashStoreProviderProps): React.JSX.Element {
  // Memoize so subscriptions stay stable for the provider's lifetime.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const store = useMemo(() => createSnapStashStore(deps ?? defaultDeps), []);
  return (
    <SnapStashStoreContext.Provider value={store}>
      {children}
    </SnapStashStoreContext.Provider>
  );
}

export function useSnapStashStore<T>(selector: (state: SnapStashStore) => T): T {
  const ctx = useContext(SnapStashStoreContext);
  if (!ctx) throw new Error('useSnapStashStore must be used inside <SnapStashStoreProvider>');
  return useStore(ctx, selector);
}
